'use client';

import { cx } from 'class-variance-authority';
import type React from 'react';
import type { ReactNode } from 'react';
import { forwardRef, useId, useRef } from 'react';
import mergeRefs from 'react-merge-refs';

export type CheckboxProps = {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  hint?: ReactNode;
  id?: string;
  label?: ReactNode;
  name?: string;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  required?: boolean;
  testID?: string;
  value?: string;
};

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked,
      defaultChecked,
      disabled,
      hasError,
      hint,
      id: possibleId,
      label,
      name,
      onBlur,
      onChange,
      readOnly,
      required,
      testID,
      value,
    }: CheckboxProps,
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const internalId = useId();
    const id = possibleId || internalId;

    return (
      <div className="flex flex-col">
        <label
          aria-describedby={hint ? `${id}-hint` : undefined}
          data-testid={testID}
          htmlFor={id}
          className={cx('text-body-2 inline-flex items-center gap-3', hasError ? 'text-error' : 'text-primary')}
        >
          <input
            aria-checked={checked}
            aria-disabled={disabled}
            aria-required={required}
            checked={checked}
            className={cx(
              'fp-checkbox',
              'appearance-none grid place-content-center',
              'h-4 w-4 min-w-4 m-0 cursor-pointer',
              'outline-offset-2 transition-all duration-100 ease-in-out',
              '[&::before]:content-[""] [&::before]:w-[0.55em] [&::before]:h-[0.55em]',
              '[&::before]:scale-0 [&::before]:transition-transform [&::before]:duration-100',
              '[&::before]:origin-bottom-left',
              '[&::before]:[clip-path:polygon(14%_44%,0_65%,50%_100%,100%_16%,80%_0%,43%_62%)]',
              '[&:checked::before]:scale-100',
              'disabled:cursor-not-allowed',
              hasError && 'fp-checkbox-error',
            )}
            defaultChecked={defaultChecked}
            disabled={disabled}
            id={id}
            name={name}
            onBlur={onBlur}
            onChange={onChange}
            readOnly={readOnly}
            ref={mergeRefs([inputRef, ref])}
            required={required}
            tabIndex={disabled ? undefined : 0}
            type="checkbox"
            value={value}
          />
          <span className="flex items-center">{label}</span>
        </label>
        {hint && (
          <button
            type="button"
            id={`${id}-hint`}
            onClick={() => inputRef.current?.click()}
            className={cx(
              'text-body-2 ml-[26px] mt-1 text-left cursor-pointer bg-transparent border-none p-0',
              hasError ? 'text-error' : 'text-tertiary',
            )}
          >
            {hint}
          </button>
        )}
      </div>
    );
  },
);

export default Checkbox;
